import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { get, isUndefined } from 'lodash';

import { FIELD_PATHS, FIELD_VALUES } from '@funnels/constants';
import { CarLoanLiability } from '@funnels/types';
import { findAssetIndex, findLiabilityIndex, initialFormValues } from '@funnels/utils';

import { useFormValues } from '../useFormValues';

interface UseVehicleDetailsProps {
  assetIndex?: number;
  liabilityIndex?: number;
}

export function useVehicleDetails(props: UseVehicleDetailsProps) {
  const [formValues, setFormValues] = useFormValues();
  const { assetIndex, liabilityIndex } = useMemo(() => {
    let assetIndex = -1;
    let liabilityIndex = -1;

    if (!isUndefined(props.assetIndex)) {
      assetIndex = props.assetIndex;
      liabilityIndex = findLiabilityIndex(formValues, props.assetIndex);
    } else if (!isUndefined(props.liabilityIndex)) {
      assetIndex = findAssetIndex(formValues, props.liabilityIndex);
      liabilityIndex = props.liabilityIndex;
    }

    return { assetIndex, liabilityIndex };
  }, [formValues, props]);
  const doYouOweAnyMoney = liabilityIndex !== -1;
  const balloonRepaymentAmount = get(formValues, FIELD_PATHS.LIABILITY_BALLOON_REPAYMENT_AMOUNT(liabilityIndex));
  const [balloonRepaymentToggle, setBalloonRepaymentToggle] = useState(!!balloonRepaymentAmount);
  const hasBalloonRepayment = balloonRepaymentAmount || balloonRepaymentToggle;
  const isNewAsset = !get(formValues, FIELD_PATHS.ASSET_ID(assetIndex));
  const assetOwnership = get(formValues, FIELD_PATHS.ASSET_PERCENT_OWNED(assetIndex));

  useEffect(() => {
    setBalloonRepaymentToggle(balloonRepaymentAmount);
  }, [assetIndex, liabilityIndex]);

  function handleDoYouOweAnyMoneyChange(e?: ChangeEvent<HTMLInputElement>) {
    const value = e?.target.value || FIELD_VALUES.FALSE;
    const liabilities: CarLoanLiability[] = [...get(formValues, FIELD_PATHS.LIABILITIES(), [])];

    if (value === FIELD_VALUES.FALSE) {
      liabilities.splice(liabilityIndex, 1);
    }

    if (value === FIELD_VALUES.TRUE) {
      const newLiability = initialFormValues.INITIAL_CAR_LOAN_LIABILITY();

      if (assetOwnership) {
        newLiability.percentOwned = { ...assetOwnership };
      }

      if (!isNewAsset) {
        newLiability.assetId = [get(formValues, FIELD_PATHS.ASSET_ID(assetIndex))];
      }

      newLiability.assetClientId = [get(formValues, FIELD_PATHS.ASSET_CLIENT_ID(assetIndex))];
      liabilities.push(newLiability);
    }

    setFormValues(FIELD_PATHS.LIABILITIES(), liabilities);
  }

  function handleHasBalloonRepaymentChange(e: ChangeEvent<HTMLInputElement>) {
    setBalloonRepaymentToggle(e.target.value === FIELD_VALUES.TRUE);
    if (e.target.value === FIELD_VALUES.FALSE) {
      setFormValues(FIELD_PATHS.LIABILITY_BALLOON_REPAYMENT_AMOUNT(liabilityIndex), undefined);
    }
  }

  return {
    doYouOweAnyMoney,
    hasBalloonRepayment,
    assetIndex,
    liabilityIndex,
    handleDoYouOweAnyMoneyChange,
    handleHasBalloonRepaymentChange,
  };
}
